import { html } from 'htm/preact'
import { useState } from 'preact/hooks'
import { route } from 'preact-router'

export function Header({ darkMode, toggleDarkMode, selectedSources, sources, isAuthenticated, user, onLoginClick, onLogout }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      route(`/search/${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
    }
  }

  return html`
    <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo & Title -->
          <div class="flex items-center">
            <a href="/" class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span class="text-white font-bold text-sm">📰</span>
              </div>
              <div>
                <h1 class="text-xl font-bold text-gray-900 dark:text-white">
                  News Aggregator
                </h1>
                ${selectedSources && sources ? html`
                  <p class="text-sm ${selectedSources.length > 0 ? 'text-gray-500 dark:text-gray-400' : 'text-red-500 dark:text-red-400'}">
                    ${selectedSources.length > 0 
                      ? `${selectedSources.length} / ${sources.filter(s => s.is_active).length} surse selectate`
                      : 'Nicio sursă selectată'}
                  </p>
                ` : null}
              </div>
            </a>
          </div>

          <!-- Desktop Navigation -->
          <nav class="hidden md:flex items-center space-x-6">
            <a href="/" class="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium">
              Acasă
            </a>
            <a href="/sources" class="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium">
              Surse
            </a>
            <a href="/admin" class="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium">
              Admin
            </a>
          </nav>

          <!-- Search & Controls -->
          <div class="flex items-center space-x-4">
            <!-- Search Form -->
            <form onSubmit=${handleSearch} class="hidden sm:block">
              <div class="relative">
                <input
                  type="text"
                  placeholder="Caută știri..."
                  value=${searchQuery}
                  onInput=${(e) => setSearchQuery(e.target.value)}
                  class="w-64 px-4 py-2 pl-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                         focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="text-gray-400">🔍</span>
                </div>
              </div>
            </form>

            <!-- Auth Button -->
            ${isAuthenticated ? html`
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">
                  👤 ${user?.username || 'User'}
                </span>
                <button
                  onClick=${onLogout}
                  class="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 
                         bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600
                         transition-colors"
                  title="Deconectare"
                >
                  Ieșire
                </button>
              </div>
            ` : html`
              <button
                onClick=${onLoginClick}
                class="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg 
                       hover:bg-primary-700 transition-colors"
              >
                🔐 Login
              </button>
            `}

            <!-- Dark Mode Toggle -->
            <button
              onClick=${toggleDarkMode}
              class="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 
                     rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              title=${darkMode ? 'Mod luminos' : 'Mod întunecat'}
            >
              ${darkMode ? '☀️' : '🌙'}
            </button>

            <!-- Mobile Menu Button -->
            <button
              onClick=${() => setMobileMenuOpen(!mobileMenuOpen)}
              class="md:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              ${mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        <!-- Mobile Menu -->
        ${mobileMenuOpen && html`
          <div class="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
            <!-- Mobile Search -->
            <form onSubmit=${handleSearch} class="mb-4">
              <input
                type="text"
                placeholder="Caută știri..."
                value=${searchQuery}
                onInput=${(e) => setSearchQuery(e.target.value)}
                class="w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                       focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </form>
            
            <!-- Mobile Navigation -->
            <nav class="space-y-2">
              <a href="/" class="block py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                Acasă
              </a>
              <a href="/sources" class="block py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                Surse
              </a>
              <a href="/admin" class="block py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                Admin
              </a>
            </nav>

            <!-- Mobile Auth -->
            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              ${isAuthenticated ? html`
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">
                    👤 ${user?.username || 'User'}
                  </span>
                  <button
                    onClick=${onLogout}
                    class="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 
                           bg-gray-100 dark:bg-gray-700 rounded-lg"
                  >
                    Ieșire
                  </button>
                </div>
              ` : html`
                <button
                  onClick=${onLoginClick}
                  class="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg"
                >
                  🔐 Login pentru scorare
                </button>
              `}
            </div>
          </div>
        `}
      </div>
    </header>
  `
}
